import { Box, IconButton, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, DotLegalPageHeader, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useEntityTemplates } from "./EntityTemplates.hooks.ts";
import React from "react";
import { EntityTemplateRowTableModel, getEntityTemplateTranslationKeys } from "./EntityTemplate.types.ts";
import CheckIcon from "@mui/icons-material/Check";
import CreateEditEntityTemplateDialog from "./createEditEntityTemplateDialog/CreateEditEntityTemplateDialog.tsx";
import { useNavigate } from "react-router";
import DeleteEntityTemplateDialog from "./deleteEntityTemplateDialog/DeleteEntityTemplateDialog.tsx";
import EmptyStateSvg from "../contract/contractsEmptyState.svg?react";
import { useUrlProvider } from "../useUrlProvider.ts";
import { useTranslation } from "../localization/useTranslation.ts";
import DotLegalTableWithControlsLocalized from "../common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized.tsx";
import ChangeEntityTemplateActiveStatusDialog from "./changeEntityTemplateActiveStatusDialog/ChangeEntityTemplateActiveStatusDialog.tsx";
import StatusChip from "../common/components/statusChip/StatusChip.tsx";
import { PurpleLockIcon } from "@common/icons";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface EntityTemplatesProps {}

function EntityTemplates(props: EntityTemplatesProps) {
    const navigate = useNavigate();
    const { getEditEntityTemplate } = useUrlProvider();

    const { translateString } = useTranslation();
    const {
        breadCrumbs,
        isLoading,
        tableData,
        hasEntityTemplates,
        getMenuItems,
        showCreateDialog,
        onSaved,
        entityTemplateForDeletion,
        setEntityTemplateForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        entityTemplateToEdit,
        entityTemplateToToggleActiveStatus,
        templateEntityType,
    } = useEntityTemplates();

    const translationKeys = getEntityTemplateTranslationKeys(templateEntityType);

    const headers = Array<ITableHeader<EntityTemplateRowTableModel>>();
    headers.push({ property: "name", text: translateString(translationKeys.template) });
    headers.push({ property: "description", text: translateString("description") });
    headers.push({ property: "isStandardTemplate", text: translateString(translationKeys.standardTemplate) });
    headers.push({ property: "active", text: translateString("status") });

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    <DotLegalButton buttonType={"primary"} size="medium" onClick={onCreateClick}>
                        {translateString(translationKeys.createTemplate)}
                    </DotLegalButton>
                }
            />
            <TablePagePaper>
                {hasEntityTemplates ? (
                    <DotLegalTableWithControlsLocalized
                        internalScroll
                        headers={headers}
                        isLoading={isLoading}
                        data={tableData}
                        emptyText={translateString(translationKeys.noTemplateMatchesQuery)}
                        defaultOrderBy={"name"}
                        getUserSpecificPageLength={() => 100}
                        clickableRows
                        hasOverflowMenu
                        renderRow={(row) => (
                            <TableRow
                                onClick={row.hasAccess ? () => navigate(getEditEntityTemplate(row.id)) : undefined}
                                key={row.id}
                                inactive={!row.active}
                                inactiveTooltip={translateString("deactivated")}
                                tooltip={row.lockedTooltip}
                                // The table sets the pointer cursor on every hovered row, so only !important overrides it.
                                sx={row.hasAccess ? undefined : { cursor: "default !important" }}
                            >
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>{row.isStandardTemplate ? <CheckIcon sx={{ height: 20 }} /> : undefined}</TableCell>
                                <TableCell>
                                    {row.active ? (
                                        <StatusChip status={"green"} value={row.activeStatusString} />
                                    ) : (
                                        <StatusChip status={"red"} value={row.activeStatusString} />
                                    )}
                                </TableCell>
                                <TableCell align={"right"}>
                                    {row.hasAccess ? (
                                        <DotLegalOverflowMenu items={getMenuItems(row)} />
                                    ) : (
                                        // Built like the overflow menu's button, so the lock sits exactly where the menu dots are on unlocked rows
                                        <IconButton disabled>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    width: 20,
                                                    height: 20,
                                                    "& svg": { height: 14, width: 14 },
                                                }}
                                            >
                                                {PurpleLockIcon}
                                            </Box>
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<EmptyStateSvg />} text={translateString(translationKeys.noTemplateText)} />
                )}
            </TablePagePaper>
            {showCreateDialog && (
                <CreateEditEntityTemplateDialog templateEntityType={templateEntityType} onCloseDialog={onDialogClose} onTemplateSaved={onSaved} />
            )}
            {entityTemplateForDeletion && (
                <DeleteEntityTemplateDialog
                    entityTemplateId={entityTemplateForDeletion.id}
                    entityTemplateName={entityTemplateForDeletion.name}
                    onCloseDialog={() => setEntityTemplateForDeletion(undefined)}
                    onDeleteSuccess={onDelete}
                />
            )}
            {entityTemplateToEdit && (
                <CreateEditEntityTemplateDialog
                    templateEntityType={templateEntityType}
                    onCloseDialog={onDialogClose}
                    onTemplateSaved={onSaved}
                    entityTemplateToEdit={entityTemplateToEdit}
                />
            )}

            {entityTemplateToToggleActiveStatus && (
                <ChangeEntityTemplateActiveStatusDialog
                    entityTemplate={entityTemplateToToggleActiveStatus}
                    templateEntityType={templateEntityType}
                    onCloseDialog={onDialogClose}
                    onToggledActiveStatus={onSaved}
                />
            )}
        </Box>
    );
}

export default EntityTemplates;
